import { Link, useSubmit } from '@remix-run/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AspectRatioInput } from '~/components/AspectRatioInput'
import { Media } from '~/components/Media/Media'
import { MediaCard } from '~/components/media-card'
import { ProductAnalytics } from '~/components/product/product-analytics'
import { getProductDisplayPrice } from '~/helpers/product'
import { makeShopOwnPath } from '~/helpers/routes'
import { getEntireStoreDiscount } from '~/helpers/shop'
import { useAnalyticsEnabled } from '~/hooks/data/use-analytics-enabled'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import { useShop } from '~/hooks/data/use-shop'
import { Badge, Skeleton } from '~/ui'
import { centsToEurosFormattedPrice } from '~/utils/number'
import { getProductCoverMedia, type ProductCardProps } from '~/utils/product'

const STATUS_TRANSLATION_KEYS = {
  draft: 'noun.draft',
  live: 'noun.live',
  presale: 'noun.pre_sale',
  preview: 'noun.coming_soon',
  archived: 'noun.archived',
} as const

export const ProductCard = ({ product, analytics }: ProductCardProps) => {
  const { t } = useTranslation()
  const shop = useShop()
  const seller = useOptionalSeller()
  const analyticsEnabled = useAnalyticsEnabled()
  const shopDiscount = useMemo(() => getEntireStoreDiscount(shop), [shop])

  const fetcherSubmit = useSubmit()
  const action = useMemo(() => {
    return makeShopOwnPath(
      shop,
      `/products/${product.id}/${product.slug}?index`
    )
  }, [shop, product])

  const price = useMemo(
    () => ({
      current: getProductDisplayPrice(product, shopDiscount || null) || 0,
      previous: shopDiscount ? getProductDisplayPrice(product, null) : null,
    }),
    [shopDiscount, product]
  )
  const [format, setFormat] = useState(product.mediaFormat || 'square')
  const media = useMemo(() => getProductCoverMedia(product), [product])

  const status = useMemo(() => {
    if (product.archivedAt !== null) return 'archived'
    if (product.isPresale) return 'presale'
    if (product.isPreview) return 'preview'
    if (!product.publishedAt && !product.isPreview && !product.isPresale)
      return 'draft'

    return 'live'
  }, [product])

  return (
    <MediaCard format={format} asChild>
      <Link
        to={makeShopOwnPath(shop, `/products/${product.id}/${product.slug}`)}
        className="ring-0!"
      >
        <div className="relative">
          <MediaCard.Ring />

          <MediaCard.Cover>
            {media && (
              <>
                <Media
                  src={media.src}
                  kind={media.kind}
                  sizes={{
                    sm: '100vw',
                    md: '50vw',
                    default: '400px',
                  }}
                />
              </>
            )}

            {seller && analyticsEnabled && (
              <MediaCard.CoverControl
                className="bottom-2 left-2"
                position="bottom-left"
              >
                <ProductAnalytics
                  analytics={analytics}
                  isAnalyticsEnabled={analyticsEnabled}
                />
              </MediaCard.CoverControl>
            )}

            {seller && (
              <MediaCard.CoverControl
                className="right-2 bottom-2"
                position="bottom-right"
              >
                <AspectRatioInput
                  value={format}
                  onChange={(newFormat) => {
                    setFormat(newFormat)
                    fetcherSubmit(
                      { mediaFormat: newFormat },
                      { method: 'PATCH', navigate: false, action }
                    )
                  }}
                />
              </MediaCard.CoverControl>
            )}
          </MediaCard.Cover>
        </div>

        <MediaCard.Content className="relative">
          <MediaCard.Title className="pr-px">
            <h3>{product.name}</h3>
            {seller && (status === 'draft' || status === 'archived') && (
              <Badge className="shrink-0">
                <Badge.Status status="neutral" />
                {t(STATUS_TRANSLATION_KEYS[status])}
              </Badge>
            )}

            {(status === 'preview' || status === 'presale') && (
              <Badge className="shrink-0">
                {t(STATUS_TRANSLATION_KEYS[status])}
              </Badge>
            )}
          </MediaCard.Title>

          <MediaCard.Description>
            <span>{centsToEurosFormattedPrice(price.current)}</span>
            {price.previous !== null && (
              <span className="ml-1.5 line-through opacity-64">
                {centsToEurosFormattedPrice(price.previous)}
              </span>
            )}
          </MediaCard.Description>
        </MediaCard.Content>
      </Link>
    </MediaCard>
  )
}

export const ProductSkeleton = () => (
  <MediaCard format="square">
    <MediaCard.Cover>
      <div className="relative h-full w-full">
        <Skeleton className="rounded-inherit absolute inset-0 h-full w-full" />
      </div>
    </MediaCard.Cover>

    <MediaCard.Content>
      <MediaCard.Title className="pr-px">
        <Skeleton className="h-5 w-3/4 rounded" />
        <Skeleton className="h-4 w-16 shrink-0 rounded-full" />
      </MediaCard.Title>

      <MediaCard.Description asChild>
        <Skeleton className="h-6 w-20 rounded" />
      </MediaCard.Description>
    </MediaCard.Content>
  </MediaCard>
)
